import { Sidebar } from './Sidebar';
import Header from '../Header';
import { MobileNavDrawer, MobileBottomNav } from './MobileNav';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { getApiBase } from '../../lib/utils';
import AppLanguageSwitcher from '../AppLanguageSwitcher';

interface AppLayoutProps {
  children: ReactNode;
  firstName?: string;
  onLogout?: () => void;
  version?: string;
  pageTitle?: string;
  currentWorkspaceId?: number;
  onWorkspaceChange?: (workspaceId: number) => void;
}

export default function AppLayout({ children, firstName, onLogout, version, pageTitle, currentWorkspaceId, onWorkspaceChange }: AppLayoutProps) {
  const { pathname } = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Pages full-height : pas de scroll sur le <main>, pas de padding, pas de footer
  const isFullHeight = /^\/messenger\/\d+$/.test(pathname) || /^\/conversations$/.test(pathname);
  // Pages avec header custom (pas de header AppLayout, pas de padding)
  const hasCustomHeader = pathname === '/agents/new' || /^\/agents\/\d+\/edit$/.test(pathname);

  return (
    <div className="flex h-screen bg-zinc-950 overflow-hidden">
      {/* Fixed Sidebar - desktop only */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 z-50 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
      }`}>
        <Sidebar onLogout={onLogout} onCollapsedChange={setSidebarCollapsed} version={version} />
      </div>

      {/* Mobile Nav Drawer */}
      <MobileNavDrawer
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        onLogout={onLogout}
      />

      {/* Main Content Area */}
      <div className={`flex flex-1 flex-col overflow-hidden transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'
      }`}>
        {/* Header (Topbar) */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center border-b border-white/5 bg-zinc-950/80 px-4 shadow-sm backdrop-blur-xl">
          {/* Mobile: hamburger + logo */}
          <div className="flex items-center gap-3 lg:hidden">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="rounded-lg p-2 text-zinc-400 hover:bg-white/5 hover:text-white transition-colors"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <MobileHeaderLogo apiBase={getApiBase()} />
          </div>

          {/* Desktop: full header */}
          <div className="hidden lg:flex flex-1 gap-x-6 self-stretch">
            <Header
              firstName={firstName}
              onLogout={onLogout}
              pageTitle={pageTitle}
              currentWorkspaceId={currentWorkspaceId}
              onWorkspaceChange={onWorkspaceChange}
            />
          </div>

          {/* Mobile: right side actions */}
          <div className="flex items-center gap-2 ml-auto lg:hidden">
            <MobileHeaderActions firstName={firstName} onLogout={onLogout} />
          </div>
        </div>

        {/* Page Content */}
        <main className={isFullHeight
          ? 'flex-1 overflow-hidden flex flex-col'
          : hasCustomHeader
            ? 'flex-1 overflow-y-auto pb-16 lg:pb-0 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent'
            : 'flex-1 overflow-y-auto pt-4 pb-16 lg:pb-0 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent flex flex-col'
        }>
          {isFullHeight ? (
            children
          ) : hasCustomHeader ? (
            children
          ) : (
            <div className="px-3 sm:px-6 lg:px-8 flex-1">
              {children}
            </div>
          )}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

function MobileHeaderLogo({ apiBase }: { apiBase: string }) {
  const [branding, setBranding] = useState<{ logo_url: string | null; site_name: string } | null>(null);

  useEffect(() => {
    fetch(`${apiBase}/global-config/public`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setBranding({
            logo_url: data.logo_url ? `${apiBase}${data.logo_url}` : null,
            site_name: data.site_name || 'Totemium',
          });
        }
      })
      .catch(() => {});
  }, [apiBase]);

  return (
    <div className="flex items-center gap-2">
      {branding?.logo_url ? (
        <img src={branding.logo_url} alt="Logo" className="h-7 w-7 object-contain" />
      ) : (
        <div className="h-7 w-7 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
          <span className="text-white font-bold text-xs">T</span>
        </div>
      )}
      <span className="font-bold text-sm text-white tracking-tight">
        {branding?.site_name || 'Totemium'}
      </span>
    </div>
  );
}

function MobileHeaderActions({ firstName }: { firstName?: string; onLogout?: () => void }) {
  const initial = (firstName || 'P').charAt(0).toUpperCase();
  return (
    <div className="flex items-center gap-2">
      <AppLanguageSwitcher />
      <div className="h-8 w-8 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
        <span className="text-emerald-400 font-semibold text-sm">{initial}</span>
      </div>
    </div>
  );
}
